import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIGroup, UIShareStatus } from '../model/UiShapes';
import { defaultGroup } from '../common/MetaData';
import { DEFAULT_GROUP_GUID } from '../common/MetaSetting';

// 内部状态接口，包含所有方法
interface GroupState {
  // State
  groups: UIGroup[];
  selectedGroupId: string;
  selectedGroup: UIGroup | null;
  status: 'idle' | 'loading' | 'failed';
  error: string | null; // 统一的错误状态

  // Actions
  setGroups: (groups: UIGroup[]) => void;
  addGroup: (group: UIGroup) => void;
  setSelectedGroupById: (groupId: string) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clear: () => void;
  updateGroup: (group: UIGroup) => void;
  changeSelectedPathByGroupId: (groupId: string) => void;
}

// 独立的actions对象
export const groupStoreActions = {
  setGroups: (set: any) => (groups: UIGroup[]) => {
    // 确保groups始终是数组类型
    if (!Array.isArray(groups)) {
      set({ groups: [] });
    } else {
      set({ groups });
    }
  },

  addGroup: (set: any) => (group: UIGroup) =>
    set((state: GroupState) => ({
      groups: [...state.groups.filter((g) => g.guid !== group.guid), group]
    })),

  setSelectedGroupById: (set: any) => (groupId: string) =>
    set((state: GroupState) => {
      // 只修改store中的值，不触发API调用
      let filteredGroup: UIGroup | undefined = defaultGroup;
      if (groupId !== DEFAULT_GROUP_GUID) filteredGroup = groupId ? state.groups.find((g) => g.guid === groupId) || undefined : undefined;
      return { selectedGroupId: groupId, selectedGroup: filteredGroup };
    }),

  setStatus: (set: any) => (status: 'idle' | 'loading' | 'failed') => set({ status }),

  setError: (set: any) => (error: string | null) => set({ error }),

  clearError: (set: any) => () => set({ error: null }),

  clear: (set: any) => () =>
    set({
      groups: [],
      selectedGroupId: '',
      selectedGroup: null,
      status: 'idle',
      error: null
    }),

  // 更新group的同步操作（不包含异步逻辑）
  updateGroup: (set: any) => (group: UIGroup) => {
    set({ status: 'loading' });

    // 更新本地状态
    set((state: GroupState) => {
      return {
        groups: state.groups.map((s) => (s.guid === group.guid ? group : s))
      };
    });

    set({ status: 'idle' });
  },

  // 选择group的同步操作（不包含异步逻辑）
  changeSelectedPathByGroupId: (set: any, get: any) => (groupId: string) => {
    set({ status: 'loading' });

    // 更新本地状态
    set((state: GroupState) => {
      const selectedGroup = groupId ? state.groups.find((s) => s.guid === groupId) : undefined;
      if (!selectedGroup) {
        return { status: 'idle' };
      }
      return {
        groups: state.groups.map((s) => (s.guid === groupId ? selectedGroup : s))
      };
    });

    // 设置选中状态
    const currentState = get();
    const selectedGroup = currentState.groups.find((group: UIGroup) => group.guid === groupId);
    if (selectedGroup) {
      set({ selectedGroup: selectedGroup });
    }

    set({ status: 'idle' });
  }
};

// 纯状态管理，移除异步操作
export const useGroupStore = create<GroupState>()(
  devtools(
    (set, get) => ({
      // Initial state
      groups: [],
      selectedGroupId: '',
      selectedGroup: null,
      status: 'idle',
      error: null, // 错误状态初始化为null

      // Actions
      setGroups: (groups: UIGroup[]) => groupStoreActions.setGroups(set)(groups),
      addGroup: (group: UIGroup) => groupStoreActions.addGroup(set)(group),
      setSelectedGroupById: (groupId: string) => groupStoreActions.setSelectedGroupById(set)(groupId),
      setStatus: (status: 'idle' | 'loading' | 'failed') => groupStoreActions.setStatus(set)(status),
      setError: (error: string | null) => groupStoreActions.setError(set)(error),
      clearError: () => groupStoreActions.clearError(set)(),
      clear: () => groupStoreActions.clear(set)(),
      updateGroup: (group: UIGroup) => groupStoreActions.updateGroup(set)(group),
      changeSelectedPathByGroupId: (groupId: string) => groupStoreActions.changeSelectedPathByGroupId(set, get)(groupId)
    }),
    {
      name: 'group-store'
    }
  )
);
